package com.mgacreative.hotel.feature.channelmanager.data

import com.mgacreative.hotel.core.network.SupabaseNetworkClient
import com.mgacreative.hotel.core.tenant.TenantSession
import io.github.jan.supabase.postgrest.postgrest
import kotlinx.serialization.Serializable

@Serializable
data class ChannelConnectionEntity(
    val id: String? = null,
    val tenant_id: String? = null,
    val channel_name: String,
    val channel_code: String,
    val is_active: Boolean = true,
    val markup_percentage: Double = 0.0,
    val api_key: String? = null,
    val hotel_id_external: String? = null,
    val sync_interval_minutes: Int = 15,
    val last_sync_at: String? = null,
    val created_at: String? = null
)

@Serializable
data class ChannelSyncLogEntity(
    val id: String? = null,
    val tenant_id: String? = null,
    val channel_name: String,
    val action_type: String,
    val message: String,
    val status: String = "SUCCESS",
    val created_at: String? = null
)

class ChannelManagerRepository(
    private val networkClient: SupabaseNetworkClient,
    private val tenantSession: TenantSession
) {
    suspend fun getChannels(): List<ChannelConnectionEntity> {
        return try {
            networkClient.client.postgrest["channel_connections"]
                .select {
                    order("created_at", io.github.jan.supabase.postgrest.query.Order.ASCENDING)
                }
                .decodeList<ChannelConnectionEntity>()
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun saveChannel(channel: ChannelConnectionEntity): ChannelConnectionEntity? {
        return try {
            val tenantId = tenantSession.currentTenantId ?: "00000000-0000-0000-0000-000000000000"
            val itemToSave = channel.copy(
                tenant_id = channel.tenant_id ?: tenantId
            )
            val result = networkClient.client.postgrest["channel_connections"]
                .upsert(itemToSave) {
                    select()
                }
                .decodeSingleOrNull<ChannelConnectionEntity>()
            result ?: itemToSave
        } catch (e: Exception) {
            null
        }
    }

    suspend fun deleteChannel(channelId: String): Boolean {
        return try {
            networkClient.client.postgrest["channel_connections"]
                .delete {
                    filter {
                        eq("id", channelId)
                    }
                }
            true
        } catch (e: Exception) {
            false
        }
    }

    suspend fun updateMarkup(channelId: String, newMarkup: Double): Boolean {
        return try {
            networkClient.client.postgrest["channel_connections"]
                .update({
                    set("markup_percentage", newMarkup)
                }) {
                    filter {
                        eq("id", channelId)
                    }
                }
            true
        } catch (e: Exception) {
            false
        }
    }

    suspend fun toggleChannelStatus(channelId: String, isActive: Boolean): Boolean {
        return try {
            networkClient.client.postgrest["channel_connections"]
                .update({
                    set("is_active", isActive)
                }) {
                    filter {
                        eq("id", channelId)
                    }
                }
            true
        } catch (e: Exception) {
            false
        }
    }

    suspend fun getLogs(): List<ChannelSyncLogEntity> {
        return try {
            networkClient.client.postgrest["channel_sync_logs"]
                .select {
                    order("created_at", io.github.jan.supabase.postgrest.query.Order.DESCENDING)
                    limit(50)
                }
                .decodeList<ChannelSyncLogEntity>()
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun addLog(channelName: String, actionType: String, message: String, status: String = "SUCCESS") {
        try {
            val tenantId = tenantSession.currentTenantId ?: "00000000-0000-0000-0000-000000000000"
            val log = ChannelSyncLogEntity(
                tenant_id = tenantId,
                channel_name = channelName,
                action_type = actionType,
                message = message,
                status = status
            )
            networkClient.client.postgrest["channel_sync_logs"]
                .insert(log)
        } catch (_: Exception) {}
    }
}
